import React, { useEffect, useRef, useContext, useLayoutEffect, useMemo } from "react";
import { Viewer, ImageryLayer } from "resium";
import { Cartesian3, Rectangle, Camera, Entity as cesiumEntity } from "cesium";
import "cesium/Source/Widgets/widgets.css";

import { RendouFunc, SyukurenFunc, FullscreenFunc } from "../cesiumfunction";
import {
  FullviewerSet,
  RendouSet,
  Map1Set,
  Map2Set,
  Zoomlevel1Set,
  Zoomlevel2Set,
  Static2Map1Set,
  Static2Map2Set,
  Rotate1Set,
  Rotate2Set,
  Area1Set,
  Area2Set,
  Cesium1Set,
  Cesium2Set,
  Color1Set,
  Color2Set,
  Shikii1Set,
  Shikii2Set,
} from "../App";
import { Ratio1Set, Ratio2Set } from "../App";
import { Year1Set, Year2Set } from "../App";
import { Alpha1Set, Alpha2Set } from "../App";
import { Zoomhanrei1Set, Zoomhanrei2Set } from "../App";
import { ModeSet } from "../App";

import { lod } from "../kashikasetting";
import { FuncOnloadZoomTenkei } from "./funcOnloadZoomTenkei";
import { AddCesiumNavigation } from "./addCesiumNavigation";
import { FunkClickFlyto } from "./funcClickFlyto";
import { csvfecth } from "../coreKashika/csvFetch";
import { FuncRotate } from "./funcRotate";
import { csvfecthforcheck } from "../coreKashika/csvFetchforcheck";
import { AddGoogleMapsApi } from "./addGoogleMapsApi";
import { AddPlateauApiChiyoda } from "./addPlateauApiChiyoda";
import { FuncZoomLatLon } from "./funcZoomLatLon";
import { FunkClickPopupInfo } from "./funcClickPopupInfo";

import { SingleTileImageryProvider } from "cesium";

// ===== 共通ユーティリティ =====
const YEAR_KEYS_15 = Array.from({ length: 15 }, (_, i) => `year${i + 1}`);
const YEAR_KEYS_10 = Array.from({ length: 10 }, (_, i) => `year${i + 1}`);

const getViewer = (rfcs) => rfcs?.current?.cesiumElement;

const normalizeNFD = (v, fallback) => {
  if (!v) return fallback;
  try {
    return v.normalize("NFD");
  } catch {
    return v;
  }
};

const hideCredit = (viewer) => {
  try {
    const el = viewer?._cesiumWidget?._creditContainer;
    if (el) el.style.display = "none";
  } catch {
    // 無視（内部実装差分で落ちないように）
  }
};

const ensureYearEntities = (viewer, dataRef) => {
  if (!viewer) return;

  // dataRef.current が無い or entity が消えてるケースを吸収
  const needsRebuild =
    !dataRef.current ||
    YEAR_KEYS_15.some((k) => {
      const e = dataRef.current?.[k];
      return !e || !viewer.entities.contains(e);
    });

  if (!needsRebuild) return;

  const map = {};
  YEAR_KEYS_15.forEach((k) => {
    map[k] = viewer.entities.add(new cesiumEntity());
  });

  // 初期表示は year5
  YEAR_KEYS_15.forEach((k) => {
    map[k].show = k === "year5";
  });

  dataRef.current = map;
};

// ===== 追加：年エンティティだけ安全に消す（壊れてたら removeAll にフォールバック）=====
const clearYearEntities = (viewer, dataRef) => {
  if (!viewer) return;
  try {
    const map = dataRef?.current;
    if (map && typeof map === "object") {
      YEAR_KEYS_15.forEach((k) => {
        const e = map[k];
        if (e && viewer.entities.contains(e)) {
          viewer.entities.remove(e);
        }
      });
      dataRef.current = null;
      return;
    }
  } catch {
    // fallthrough
  }
  try {
    viewer.entities.removeAll();
  } catch {
    //
  }
  if (dataRef) dataRef.current = null;
};

/**
 * ★「初回だけ year5」ロジック込み
 * - selectedYearValue が未確定（"" / 0 / undefined 等）でも初回は year5 を表示
 * - ただし一度でも有効な年が選ばれたら、その後は last を優先し year5 固定にならない
 * - 3000 は常に非表示
 */
const applyYearVisibility = (viewer, dataRef, metadata, selectedYearValue, lastKeyRef) => {
  if (!viewer || !dataRef.current || !metadata) return;

  // 「metadata.yearX の値」→「yearX 文字列」
  const yearSetMapping = Object.create(null);
  YEAR_KEYS_15.forEach((k) => {
    const v = metadata[k];
    if (v === undefined || v === null) return;
    yearSetMapping[String(v)] = k;
  });

  // 既存仕様の特殊値
  yearSetMapping["3000"] = null;

  const sv = selectedYearValue;
  const svStr = sv === undefined || sv === null ? "" : String(sv);

  // 通常解決
  let targetKey = yearSetMapping[svStr];

  // 未確定（空/0）なら「last があれば last、なければ year5」
  if ((svStr === "" || svStr === "0") && !targetKey) {
    targetKey = (lastKeyRef && lastKeyRef.current) ? lastKeyRef.current : "year5";
  }

  // 解決できた時だけ last を更新（null は更新しない）
  if (targetKey && lastKeyRef) {
    lastKeyRef.current = targetKey;
  }

  YEAR_KEYS_15.forEach((k) => {
    const ent = dataRef.current[k];
    if (ent) ent.show = targetKey ? k === targetKey : false;
  });
};

const loadCsvSeries = ({
  rfcs,
  dataRef,
  metadata,
  area,
  ratio,
  alpha,
  color,
  shikii,
  csvNameFallback = "人口総数×人口総数",
  doFileCheckAtYear = 5,
  years = YEAR_KEYS_10,
  nihonsokuchiRule = (md, i) => {
    const yearKey = `year${i}`;
    return i <= 8 ? (md?.[`${yearKey}_nihonsokuchi`] || 0) * (md?.[yearKey] || 0) : 0;
  },
}) => {
  const viewer = getViewer(rfcs);
  if (!viewer || !metadata) return;

  ensureYearEntities(viewer, dataRef);

  const csv_name = normalizeNFD(metadata?.csv_filename, csvNameFallback);
  const areaN = normalizeNFD(area, "福岡県糸島市");

  years.forEach((yearKey) => {
    const i = Number(yearKey.replace("year", ""));
    const yearNum = metadata?.[yearKey];
    if (!yearNum) return;

    csvfecth(
      rfcs,
      dataRef.current[yearKey],
      metadata.no,
      yearNum,
      areaN,
      csv_name,
      nihonsokuchiRule(metadata, i),
      metadata,
      ratio,
      alpha,
      color,
      shikii,
      i === 1 ? yearNum : 0
    );

    if (i === doFileCheckAtYear) {
      csvfecthforcheck(
        rfcs,
        dataRef.current[yearKey],
        metadata.no,
        yearNum,
        areaN,
        csv_name,
        0,
        metadata,
        ratio,
        alpha,
        color,
        shikii
      );
    }
  });
};

const useMoveEndZoomReset = ({
  rfcs,
  isEnabled,
  modeset,
  lod,
  rendouset,
  setZoomlevelSelf,
  setZoomhanreiSelf,
  setZoomlevelOther,
  setZoomhanreiOther,
}) => {
  useEffect(() => {
    if (!isEnabled) return;

    const viewer = getViewer(rfcs);
    if (!viewer?.camera?.moveEnd) return;

    const handler = () => {
      const height = viewer.camera.positionCartographic?.height ?? 0;
      const threshold = modeset == 5 ? lod?.[1] : lod?.[4];

      if (threshold != null && height >= threshold) {
        setZoomlevelSelf(1);
        setZoomhanreiSelf(null);

        if (rendouset) {
          setZoomlevelOther(1);
          setZoomhanreiOther(null);
        }
      }
    };

    viewer.camera.moveEnd.addEventListener(handler);
    return () => {
      try {
        viewer.camera.moveEnd.removeEventListener(handler);
      } catch {
        //
      }
    };
  }, [
    rfcs,
    isEnabled,
    modeset,
    rendouset,
    lod,
    setZoomlevelSelf,
    setZoomhanreiSelf,
    setZoomlevelOther,
    setZoomhanreiOther,
  ]);
};

// ===== 本体 =====
const position = Cartesian3.fromDegrees(135.0707383, 35.7117244, 100);

export const CesiumContainer = (props) => {
  // location.search を安全に評価
  let locationsetting = [];
  try {
    locationsetting = decodeURI(window.location.search || "").split("_");
  } catch {
    locationsetting = String(window.location.search || "").split("_");
  }
  console.log(locationsetting);

  // Camera初期範囲（現状維持）
  const extent = Rectangle.fromDegrees(129.9999999999, -89.99999999, 130, -89.999999999);
  Camera.DEFAULT_VIEW_RECTANGLE = extent;

  const rfcs1 = useRef(null);
  const rfcs2 = useRef(null);

  // year entity map
  const data1Ref = useRef(null);
  const data2Ref = useRef(null);

  // ★「初回だけ year5」用：最後に有効だったyearキーを覚える
  const lastYearKey1Ref = useRef("year5");
  const lastYearKey2Ref = useRef("year5");

  // Contexts
  const { setCesium1Set } = useContext(Cesium1Set);
  const { setCesium2Set } = useContext(Cesium2Set);

  const { fullviewerset } = useContext(FullviewerSet);
  const { rendouset } = useContext(RendouSet);

  const { map1set } = useContext(Map1Set);
  const { map2set } = useContext(Map2Set);

  const { setZoomlevel1Set } = useContext(Zoomlevel1Set);
  const { setZoomlevel2Set } = useContext(Zoomlevel2Set);
  const { setZoomhanrei1Set } = useContext(Zoomhanrei1Set);
  const { setZoomhanrei2Set } = useContext(Zoomhanrei2Set);

  const { static2map1set } = useContext(Static2Map1Set);
  const { static2map2set } = useContext(Static2Map2Set);

  const { rotate1set } = useContext(Rotate1Set);
  const { rotate2set } = useContext(Rotate2Set);

  const { area1set } = useContext(Area1Set);
  const { area2set } = useContext(Area2Set);

  const { ratio1set } = useContext(Ratio1Set);
  const { ratio2set } = useContext(Ratio2Set);

  const { year1set } = useContext(Year1Set);
  const { year2set } = useContext(Year2Set);

  const { alpha1set } = useContext(Alpha1Set);
  const { alpha2set } = useContext(Alpha2Set);

  const { color1set } = useContext(Color1Set);
  const { color2set } = useContext(Color2Set);

  const { shikii1set } = useContext(Shikii1Set);
  const { shikii2set } = useContext(Shikii2Set);

  const { modeset } = useContext(ModeSet);

  // credit非表示
  useLayoutEffect(() => {
    const v1 = getViewer(rfcs1);
    const v2 = getViewer(rfcs2);
    if (v1) hideCredit(v1);
    if (v2) hideCredit(v2);
  }, []);

  // App側へ ref を渡す（初回のみ）
  useEffect(() => {
    setCesium1Set(rfcs1);
    setCesium2Set(rfcs2);
  }, [setCesium1Set, setCesium2Set]);

  // moveEnd（ズームしきい値で凡例/ズームレベルを戻す）
  useMoveEndZoomReset({
    rfcs: rfcs1,
    isEnabled: true,
    modeset,
    lod,
    rendouset,
    setZoomlevelSelf: setZoomlevel1Set,
    setZoomhanreiSelf: setZoomhanrei1Set,
    setZoomlevelOther: setZoomlevel2Set,
    setZoomhanreiOther: setZoomhanrei2Set,
  });

  useMoveEndZoomReset({
    rfcs: rfcs2,
    isEnabled: true,
    modeset,
    lod,
    rendouset,
    setZoomlevelSelf: setZoomlevel2Set,
    setZoomhanreiSelf: setZoomhanrei2Set,
    setZoomlevelOther: setZoomlevel1Set,
    setZoomhanreiOther: setZoomhanrei1Set,
  });

  // year entity 初期生成（左右）
  useLayoutEffect(() => {
    const viewer = getViewer(rfcs1);
    if (!viewer) return;
    ensureYearEntities(viewer, data1Ref);
  }, []);

  useLayoutEffect(() => {
    const viewer = getViewer(rfcs2);
    if (!viewer) return;
    ensureYearEntities(viewer, data2Ref);
  }, []);

  // ★ 検証用：area1 変更時に「全部」removeAll（左）
  useEffect(() => {
    const viewer = getViewer(rfcs1);
    if (!viewer) return;
    if (!area1set) return;
    console.log("[removeAll TEST] LEFT area changed:", area1set);
    try {
      viewer.entities.removeAll();
    } catch (e) {
      console.warn("[removeAll TEST] LEFT removeAll failed:", e);
    }
    data1Ref.current = null;
    ensureYearEntities(viewer, data1Ref);
  }, [area1set]);
  // ★ 検証用：area2 変更時に「全部」removeAll（右）
  useEffect(() => {
    const viewer = getViewer(rfcs2);
    if (!viewer) return;
    if (!area2set) return;
    console.log("[removeAll TEST] RIGHT area changed:", area2set);
    try {
      viewer.entities.removeAll();
    } catch (e) {
      console.warn("[removeAll TEST] RIGHT removeAll failed:", e);
    }
    data2Ref.current = null;
    ensureYearEntities(viewer, data2Ref);
  }, [area2set]);

  // 左：可視化（テーマ/都市/タブ切替など）
  useEffect(() => {
    const viewer = getViewer(rfcs1);
    if (!viewer) return;

    const metadata = static2map1set;
    if (!metadata) return;

    loadCsvSeries({
      rfcs: rfcs1,
      dataRef: data1Ref,
      metadata,
      area: area1set || "福岡県糸島市",
      ratio: ratio1set,
      alpha: alpha1set,
      color: color1set,
      shikii: shikii1set,
      years: YEAR_KEYS_10,
      doFileCheckAtYear: 5,
      nihonsokuchiRule: (md, i) => {
        const yearKey = `year${i}`;
        const yearNum = md?.[yearKey] || 0;
        return i <= 8 ? (md?.[`${yearKey}_nihonsokuchi`] || 0) * yearNum : 0;
      },
    });

    // ★removeAll は復活させない（ユーザー指示）
    return () => {
      //
    };
  }, [static2map1set, area1set, ratio1set, alpha1set, color1set, shikii1set]);

  // 左：表示ON/OFF（year切替）
  useEffect(() => {
    const viewer = getViewer(rfcs1);
    if (!viewer) return;

    const metadata = static2map1set;
    if (!metadata) return;

    ensureYearEntities(viewer, data1Ref);
    applyYearVisibility(viewer, data1Ref, metadata, year1set, lastYearKey1Ref);
  }, [static2map1set, year1set]);

  // 右：可視化（modeset条件あり）
  useEffect(() => {
    const viewer = getViewer(rfcs2);
    if (!viewer) return;

    const metadata = static2map2set;
    if (!metadata) return;

    const enabled = modeset < 10;
    if (!enabled) return;

    const years = [
      "year1",
      "year2",
      "year3",
      "year4",
      "year5",
      "year6",
      "year7",
      "year8",
    ];

    loadCsvSeries({
      rfcs: rfcs2,
      dataRef: data2Ref,
      metadata,
      area: area2set || "福岡県糸島市",
      ratio: ratio2set,
      alpha: alpha2set,
      color: color2set,
      shikii: shikii2set,
      years,
      doFileCheckAtYear: -1,
      nihonsokuchiRule: (md, i) => {
        const yearKey = `year${i}`;
        const yearNum = md?.[yearKey] || 0;
        return (md?.[`${yearKey}_nihonsokuchi`] || 0) * yearNum;
      },
    });

    // ★removeAll は復活させない（ユーザー指示）
    return () => {
      //
    };
  }, [static2map2set, area2set, ratio2set, alpha2set, color2set, shikii2set, modeset]);

  // 右：表示ON/OFF（year切替）
  useEffect(() => {
    const viewer = getViewer(rfcs2);
    if (!viewer) return;

    const metadata = static2map2set;
    if (!metadata) return;

    ensureYearEntities(viewer, data2Ref);
    applyYearVisibility(viewer, data2Ref, metadata, year2set, lastYearKey2Ref);
  }, [static2map2set, year2set]);

  // 既存のエフェクト（現状維持）
  FullscreenFunc(rfcs1, rfcs2);
  RendouFunc(rfcs1, rfcs2);
  SyukurenFunc(rfcs1, rfcs2);

  // blank imagery provider（現状維持）
  const blankImageURI =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAkQBADs=";

  const blankImageryProvider = useMemo(() => {
    return new SingleTileImageryProvider({ url: blankImageURI });
  }, []);

  return (
    <div className="overflow-hidden">
      {!(modeset == 7777777 || modeset == 7777778) && (
        <>
          <FuncOnloadZoomTenkei rfcs={rfcs1} rfcs2={rfcs2} />
          <FunkClickFlyto rfcs={rfcs1} defratio={static2map1set} valratio={ratio1set} />
          <FunkClickFlyto rfcs={rfcs2} defratio={static2map2set} valratio={ratio2set} />
        </>
      )}

      <FuncRotate rfcs={rfcs1} rotateset={rotate1set} />
      <FuncRotate rfcs={rfcs2} rotateset={rotate2set} />

      {modeset != 9999999 && <AddCesiumNavigation rfcs={rfcs1} />}

      {modeset == 7777777 && (
        <>
          <AddGoogleMapsApi rfcs={rfcs2} />
          <AddPlateauApiChiyoda rfcs={rfcs1} />
          <FuncZoomLatLon rfcs={rfcs1} areaset={area1set} />
          <FuncZoomLatLon rfcs={rfcs2} areaset={area1set} />
          <FunkClickPopupInfo rfcs={rfcs1} defratio={static2map1set} valratio={ratio1set} />
        </>
      )}

      {modeset == 7777778 && (
        <>
          <AddGoogleMapsApi rfcs={rfcs2} />
          <AddPlateauApiChiyoda rfcs={rfcs2} />
          <FuncZoomLatLon rfcs={rfcs1} areaset={area1set} />
          <FuncZoomLatLon rfcs={rfcs2} areaset={area1set} />
          <FunkClickPopupInfo rfcs={rfcs1} defratio={static2map1set} valratio={ratio1set} />
        </>
      )}

      <Viewer
        ref={rfcs1}
        className={fullviewerset ? "z-10" : "z-10 w-1/2 ml-auto"}
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        vrButton={false}
        homeButton={false}
        timeline={false}
        navigationHelpButton={false}
        fullscreenButton={true}
        baseLayerPicker={false}
        geocoder={false}
        imageryProvider={blankImageryProvider}
        scene3DOnly={true}
        animation={false}
        creditContainer={null}
        selectionIndicator={false}
        sceneModePicker={false}
        infoBox={false}
        shadows={false}
      >
        <ImageryLayer imageryProvider={map1set} />
      </Viewer>

      <Viewer
        ref={rfcs2}
        className="w-2/4 mr-auto"
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        vrButton={false}
        homeButton={false}
        timeline={false}
        navigationHelpButton={false}
        baseLayerPicker={false}
        fullscreenButton={false}
        geocoder={false}
        imageryProvider={blankImageryProvider}
        scene3DOnly={true}
        animation={false}
        creditContainer={null}
        selectionIndicator={false}
        sceneModePicker={false}
        infoBox={false}
        shadows={false}
      >
        <ImageryLayer imageryProvider={map2set} />
      </Viewer>
    </div>
  );
};
